import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Textarea } from 'designSystem/components/Textarea'
import { Send, Square } from 'lucide-react'
import React, { useState } from 'react'
import styled from 'styled-components'

const Row = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem 0.75rem;
  flex-shrink: 0;
  align-items: flex-end;
  textarea {
    flex: 1;
    resize: none;
  }
`

const Pill = styled(Button)`
  height: auto;
  border-radius: 999px;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  white-space: normal;
  text-align: left;
`

const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem 0;
  flex-shrink: 0;
`

/**
 * Where a message is written. Enter sends; shift-enter is a new line. Any
 * suggestions render as pills above the box and send themselves on click.
 */
export const Composer: React.FC<{
  disabled: boolean
  sending: boolean
  onSend: (text: string) => void
  onStop: () => void
  suggestions?: string[]
}> = ({ disabled, sending, onSend, onStop, suggestions }) => {
  const { t } = useLingui()
  const [text, setText] = useState('')

  const submit = () => {
    const value = text.trim()
    if (!value || disabled) return
    setText('')
    onSend(value)
  }

  return (
    <>
      {suggestions && suggestions.length > 0 && (
        <Suggestions>
          {suggestions.map((suggestion) => (
            <Pill
              key={suggestion}
              variant='outline'
              size='xs'
              disabled={disabled || sending}
              title={t`Send this prompt`}
              onClick={() => onSend(suggestion)}>
              {suggestion}
            </Pill>
          ))}
        </Suggestions>
      )}
      <Row>
        <Textarea
          value={text}
          rows={2}
          disabled={disabled}
          placeholder={t`Ask for a model, an edit, or an explanation…`}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              submit()
            }
          }}
        />
        {sending ? (
          <Button
            variant='outline'
            onClick={onStop}
            aria-label={t`Stop the agent`}>
            <Square data-icon='inline-start' />
            {t`Stop`}
          </Button>
        ) : (
          <Button
            variant='primaryRaised'
            onClick={submit}
            disabled={disabled || !text.trim()}
            aria-label={t`Send`}>
            <Send data-icon='inline-start' />
            {t`Send`}
          </Button>
        )}
      </Row>
    </>
  )
}
